import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { awaitDispatch } from '@vk-hr-tek/core/redux';
import {
  showCustomNotification,
  showNotification,
} from '@vk-hr-tek/core/notifications';

import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { Entity } from '@app/types';
import {
  AccountBusinessTripsExceptionsRequest,
  AccountBusinessTripsExceptionsResponse,
  AccountService,
  DeleteCompanyExceptionsFromAccountBusinessTripsParams,
  GetAccountBusinessTripsExceptionsParams,
  GetAccountBusinessTripsExceptionsQuery,
  UpdateAccountBusinessTripsExceptionsParams,
  UpdateCompanyBusinessTripsExceptionParams,
  UpdateCompanyBusinessTripsExceptionRequest,
} from '@app/gen/account';
import { ThunkExtra, ThunkValuesAndActions } from '@app/store';

import {
  accountBusinessTripsExceptionsAdapter,
  SettingsState,
  SettingsWithRootState,
} from '../../settings.state';

import { getAccounts } from './account.actions';

export const getAccountBusinessTripsExceptions = createAsyncThunk<
  AccountBusinessTripsExceptionsResponse,
  GetAccountBusinessTripsExceptionsQuery &
    GetAccountBusinessTripsExceptionsParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAccountBusinessTripsExceptions',
  async (
    { account_id, ...query },
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    try {
      const state = getState();

      if (state.settings.accounts.status === 'idle') {
        await awaitDispatch(dispatch(getAccounts()));
      }

      const result = await inject(
        AccountService,
      ).getAccountBusinessTripsExceptions({
        params: {
          account_id,
        },
        query,
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const addAccountBusinessTripsExceptions = createAsyncThunk<
  void,
  ThunkValuesAndActions<
    UpdateAccountBusinessTripsExceptionsParams &
      AccountBusinessTripsExceptionsRequest
  >,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/addAccountBusinessTripsExceptions',
  async (
    { values: { company_ids, account_id }, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    const exceptionsCount = company_ids.length;

    try {
      const accountService = inject(AccountService);

      await accountService.updateAccountBusinessTripsExceptions({
        params: { account_id },
        body: { company_ids },
      });

      actions.resolve(null);

      dispatch(
        showNotification(`Исключения успешно добавлены (${exceptionsCount})`),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: `Не удалось добавить исключения (${exceptionsCount})`,
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );

      actions.reject({ [FORM_ERROR]: err });

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateCompanyBusinessTripsException = createAsyncThunk<
  void,
  UpdateCompanyBusinessTripsExceptionParams &
    UpdateCompanyBusinessTripsExceptionRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateCompanyBusinessTripsException',
  async (
    { account_id, company_id, business_trips_enabled },
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    try {
      const state = getState();

      await inject(AccountService).updateCompanyBusinessTripsException({
        params: { account_id, company_id },
        body: { business_trips_enabled },
      });

      const company =
        state.settings.accountBusinessTripsExceptions.entities[company_id];

      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: `Раздел «Командировки» ${
              business_trips_enabled ? 'включен' : 'выключен'
            } в компании`,
            message: company?.name ?? '',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: `Не удалось ${
              business_trips_enabled ? 'включить' : 'выключить'
            } раздел «Командировки» в компании`,
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const deleteAccountBusinessTripsException = createAsyncThunk<
  void,
  DeleteCompanyExceptionsFromAccountBusinessTripsParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/deleteAccountBusinessTripsException',
  async (
    params,
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    try {
      const state = getState();

      await inject(
        AccountService,
      ).deleteCompanyExceptionsFromAccountBusinessTrips({
        params,
      });

      const company =
        state.settings.accountBusinessTripsExceptions.entities[
          params.company_id
        ];

      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: 'Компания успешно удалена из списка исключений',
            message: company?.name ?? '',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось удалить компанию из списка исключений',
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAddAccountBusinessTripsExceptionsInfo = createAsyncThunk<
  void,
  string,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAddAccountBusinessTripsExceptionsInfo',
  async (accountId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();

      await Promise.all([
        state.settings.accounts.status === 'idle' &&
          awaitDispatch(dispatch(getAccounts())),
        awaitDispatch(
          dispatch(
            getAccountBusinessTripsExceptions({
              account_id: accountId,
            }),
          ),
        ),
      ]);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsAccountBusinessTripsReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getAccountBusinessTripsExceptions.pending, (state) => {
    state.accountBusinessTripsExceptions.status = 'loading';
    state.accountBusinessTripsExceptions.error = null;
  });
  builder.addCase(
    getAccountBusinessTripsExceptions.fulfilled,
    (state, { payload }) => {
      if (payload) {
        accountBusinessTripsExceptionsAdapter.upsertMany(
          state.accountBusinessTripsExceptions,
          payload.companies,
        );
        state.accountBusinessTripsExceptions.currentIds = payload.companies.map(
          ({ id }) => id,
        );
      }

      state.accountBusinessTripsExceptions.total = payload.total_count;
      state.accountBusinessTripsExceptions.status = 'complete';
      state.accountBusinessTripsExceptions.error = null;
    },
  );
  builder.addCase(
    getAccountBusinessTripsExceptions.rejected,
    (state, { payload, error }) => {
      state.accountBusinessTripsExceptions.status = 'failed';
      state.accountBusinessTripsExceptions.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );

  builder.addCase(
    updateCompanyBusinessTripsException.fulfilled,
    (state, { meta }) => {
      accountBusinessTripsExceptionsAdapter.updateOne(
        state.accountBusinessTripsExceptions,
        {
          id: meta.arg.company_id,
          changes: { business_trips_enabled: meta.arg.business_trips_enabled },
        },
      );
    },
  );

  builder.addCase(
    deleteAccountBusinessTripsException.fulfilled,
    (state, { meta }) => {
      accountBusinessTripsExceptionsAdapter.removeOne(
        state.accountBusinessTripsExceptions,
        meta.arg.company_id,
      );
      state.accountBusinessTripsExceptions.total =
        state.accountBusinessTripsExceptions.total - 1;
    },
  );

  builder.addCase(getAddAccountBusinessTripsExceptionsInfo.pending, (state) => {
    state.addAccountBusinessTripsExceptions.status = 'loading';
    state.addAccountBusinessTripsExceptions.error = null;
  });
  builder.addCase(
    getAddAccountBusinessTripsExceptionsInfo.fulfilled,
    (state) => {
      state.addAccountBusinessTripsExceptions.status = 'complete';
      state.addAccountBusinessTripsExceptions.error = null;
    },
  );
  builder.addCase(
    getAddAccountBusinessTripsExceptionsInfo.rejected,
    (state, { payload, error }) => {
      state.addAccountBusinessTripsExceptions.status = 'failed';
      state.addAccountBusinessTripsExceptions.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
};
